import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";

/* Hooks */
import { useThunk } from "../../hooks/useThunk";
import { getSignedUrl } from "../../store";

/* Preview Player context */
import { usePreviewPlayer } from "../../contexts/PreviewPlayerContext";

/* Assets */
import LogoImage from "../../assets/icons/Logo 2.webp";
import BackwardIcon from "../../assets/icons/player/backward.svg";
import PlayIcon from "../../assets/icons/player/play.svg";
import PauseIcon from "../../assets/icons/player/pause.svg";
import ForwardIcon from "../../assets/icons/player/forward.svg";
import LoadingIcon from "../../assets/icons/player/loading.svg";

/**
 * Helper function to extract track name from preview data
 */
const extractTrackName = (preview) => {
  return (
    preview?.trackName ||
    preview?.name ||
    preview?.file?.name ||
    "Preview Track"
  );
};

/**
 * PreviewPlayer - Persistent bottom audio player for preview tracks
 * Simplified version of SupremeEditsPlayer with only:
 * - Play, Next, Previous buttons
 * - Preview name
 * - Waveform for seeking
 */
const PreviewPlayer = () => {
  // Refs for waveform container and WaveSurfer instance
  const waveformRef = useRef(null);
  const wavesurfer = useRef(null);
  const lastClickRef = useRef(Date.now());
  const isLoadingRef = useRef(false);
  const autoAdvanceRef = useRef(true);

  const [doGetSignedUrl] = useThunk(getSignedUrl);

  // Context values from Preview Player context
  const {
    previewList,
    currentIndex,
    setCurrentIndex,
    currentPreview,
    setCurrentPreview,
    isPlaying,
    setIsPlaying,
    handleNext,
    handlePrev,
  } = usePreviewPlayer();

  // State variables for player functionality
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [autoAdvance, setAutoAdvance] = useState(true); // Autoplay next on finish (like SupremeEditsPlayer)

  useEffect(() => {
    autoAdvanceRef.current = autoAdvance;
  }, [autoAdvance]);

  // Update current preview when index changes
  useEffect(() => {
    if (previewList && previewList.length > 0 && previewList[currentIndex]) {
      setCurrentPreview(previewList[currentIndex]);
    }
  }, [currentIndex, previewList, setCurrentPreview]);

  // Helper function to format time in mm:ss
  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const min = Math.floor(time / 60);
    const sec = Math.floor(time % 60);
    return `${min.toString().padStart(2, "0")}:${sec
      .toString()
      .padStart(2, "0")}`;
  };

  // Load audio track when currentPreview changes
  useEffect(() => {
    const audioUrl = currentPreview?.url || currentPreview?.file?.url;

    if (!audioUrl) {
      return;
    }

    // Prevent concurrent loads
    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;
    setAudioLoaded(false);
    setCurrentTime(0);
    setDuration(0);

    // Destroy existing instance if it exists
    if (wavesurfer.current) {
      try {
        wavesurfer.current.destroy();
      } catch (e) {
        console.warn("Error destroying wavesurfer:", e);
      }
      wavesurfer.current = null;
    }

    const container = waveformRef.current;
    if (!container) {
      isLoadingRef.current = false;
      return;
    }

    const commonOptions = {
      waveColor: "#8D8D8D",
      progressColor: [
        "#52FAF0",
        "#197AD0",
        "#197AD0",
        "#197AD0",
        "#197AD0",
        "#197AD0",
        "#197AD0",
      ],
      barWidth: 2,
      barGap: 3,
      barRadius: 15,
      height: 30,
      cursorWidth: 0,
      normalize: true,
      responsive: true,
      sampleRate: 3000,
      hideScrollbar: true,
    };

    // Create WaveSurfer instance
    try {
      wavesurfer.current = WaveSurfer.create({
        ...commonOptions,
        container: container,
      });
    } catch (error) {
      console.error("Failed to create WaveSurfer instance:", error);
      isLoadingRef.current = false;
      return;
    }

    const loadTrack = async () => {
      try {
        const peaks = currentPreview?.peaks || currentPreview?.file?.peaks;

        // Set up event handlers
        wavesurfer.current.on("ready", () => {
          setAudioLoaded(true);
          setDuration(wavesurfer.current.getDuration());
          isLoadingRef.current = false;

          // Auto-play if isPlaying is true
          if (isPlaying) {
            wavesurfer.current.play().catch(() => {});
          }
        });

        wavesurfer.current.on("play", () => setIsPlaying(true));
        wavesurfer.current.on("pause", () => setIsPlaying(false));

        wavesurfer.current.on("audioprocess", () => {
          if (wavesurfer.current && wavesurfer.current.isPlaying()) {
            setCurrentTime(wavesurfer.current.getCurrentTime());
          }
        });

        wavesurfer.current.on("finish", () => {
          setIsPlaying(false);
          // Auto advance to next track when Autoplay is on
          if (autoAdvanceRef.current && currentIndex < previewList.length - 1) {
            setTimeout(() => {
              setCurrentIndex((i) => (i < previewList.length - 1 ? i + 1 : i));
              setIsPlaying(true);
            }, 300);
          }
        });

        wavesurfer.current.on("error", (err) => {
          console.error("WaveSurfer error:", err);
          setAudioLoaded(false);
          isLoadingRef.current = false;
        });

        // Fetch signed URL for authenticated access
        const result = await doGetSignedUrl({ path: audioUrl });
        const signedUrl = result?.response?.data?.url;

        if (!result.success || !signedUrl) {
          throw new Error("Failed to fetch signed URL for preview audio");
        }

        // Load the audio
        if (peaks && Array.isArray(peaks) && peaks.length > 0) {
          await wavesurfer.current.load(signedUrl, peaks);
        } else {
          await wavesurfer.current.load(signedUrl);
        }
      } catch (error) {
        console.error("Error loading preview audio:", error);
        setAudioLoaded(false);
        isLoadingRef.current = false;
      }
    };

    loadTrack();

    return () => {
      isLoadingRef.current = false;
      if (wavesurfer.current) {
        try {
          wavesurfer.current.destroy();
        } catch (e) {
          console.warn("Error destroying wavesurfer on cleanup:", e);
        }
        wavesurfer.current = null;
      }
    };
  }, [currentPreview?.url, currentPreview?.file?.url, doGetSignedUrl]);

  // Handle play/pause state changes when isPlaying changes from context
  useEffect(() => {
    if (!audioLoaded || !wavesurfer.current) return;

    if (isPlaying && !wavesurfer.current.isPlaying()) {
      wavesurfer.current.play().catch(() => {});
    } else if (!isPlaying && wavesurfer.current.isPlaying()) {
      wavesurfer.current.pause();
    }
  }, [isPlaying, audioLoaded]);

  // Function to toggle play/pause
  const togglePlay = () => {
    if (!wavesurfer.current || !audioLoaded || isLoadingRef.current) return;

    const isPlayingNow = wavesurfer.current.isPlaying();

    if (isPlayingNow) {
      wavesurfer.current.pause();
      setIsPlaying(false);
    } else {
      wavesurfer.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const handleSafeMove = (direction) => {
    const now = Date.now();
    if (now - lastClickRef.current < 300) return;
    lastClickRef.current = now;

    if (direction === "next") {
      handleNext();
    } else {
      handlePrev();
    }
  };

  // Don't render if no previews
  if (!currentPreview || !previewList || previewList.length === 0) return null;

  return (
    <main
      className="fixed bottom-[50px] lg:bottom-0 left-0 w-screen z-[999] p-0 select-none"
      role="region"
      aria-label="Preview Audio Player"
    >
      <div
        className="fixed bottom-[40px] lg:bottom-0 left-0 w-screen h-fit z-[999] animate-bottom-to-top bg-transparent p-0 select-none"
        style={{ boxShadow: "0px -4px 22px 0px #000000A6" }}
      >
        <div className="w-full z-[999] bg-[#070D17]/50 backdrop-blur-md">
          <div className="relative flex items-center justify-between w-full h-full text-white">
            <div className="flex w-full flex-col lg:flex-row lg:gap-40">
              {/* LEFT: cover + track info (like SupremeEditsPlayer) */}
              <div className="flex items-center gap-0 xs:gap-1 sm:gap-2 order-2 lg:order-1 min-w-0 max-w-full overflow-hidden lg:min-w-0">
                {/* COVER IMAGE */}
                <div className="relative block w-full sm:w-[108px] h-[70px] sm:h-[108px] flex-shrink-0 max-w-[84px] sm:max-w-[108px] overflow-hidden rounded-none">
                  {currentPreview?.coverImage?.url ? (
                    <img
                      src={currentPreview.coverImage.url}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src={LogoImage}
                      alt="Logo"
                      className="absolute inset-0 w-full h-full object-contain p-3"
                    />
                  )}
                </div>

                {/* TEXT INFO */}
                <div className="min-w-0 flex-1 pl-3 sm:pl-2 overflow-hidden">
                  <p className="text-[10px] sm:text-sm text-gradient font-normal truncate">
                    Preview {currentIndex + 1} of {previewList.length}
                  </p>
                  <p className="text-sm sm:text-base font-bold truncate text-secondary leading-[135%] my-[1px]">
                    {currentPreview?.name}
                  </p>
                  <p className="text-tertiary truncate text-[10px] sm:text-sm">
                    (Preview)
                  </p>
                </div>

                {/* MOBILE PLAYER CONTROLS */}
                <div className="flex min-w-fit justify-end items-center px-2 flex-shrink-0 lg:hidden">
                  <div className="grid grid-cols-3 h-full w-fit gap-1 xs:gap-2">
                    <button
                      onClick={() => handleSafeMove("prev")}
                      disabled={currentIndex === 0}
                      aria-label="Previous"
                      className="p-20 xs:p-2 hover:opacity-70 active:scale-75 transition"
                    >
                      <img
                        src={BackwardIcon}
                        alt=""
                        className="w-3 h-3 xs:w-4 xs:h-4"
                      />
                    </button>

                    {!audioLoaded ? (
                      <div className="flex items-center justify-center h-full opacity-50">
                        <img
                          src={LoadingIcon}
                          alt="Loading"
                          className="w-3 h-3 xs:w-4 xs:h-4 animate-spin"
                        />
                      </div>
                    ) : (
                      <button
                        onClick={togglePlay}
                        aria-label={isPlaying ? "Pause" : "Play"}
                        className="p-1 xs:p-2 hover:opacity-70 active:scale-75 transition"
                      >
                        {isPlaying ? (
                          <img
                            src={PauseIcon}
                            alt="Pause"
                            className="w-3 h-3 xs:w-4 xs:h-4"
                          />
                        ) : (
                          <img
                            src={PlayIcon}
                            alt="Play"
                            className="w-3 h-3 xs:w-4 xs:h-4"
                          />
                        )}
                      </button>
                    )}

                    <button
                      onClick={() => handleSafeMove("next")}
                      disabled={currentIndex === previewList.length - 1}
                      aria-label="Next"
                      className="p-1 xs:p-2 hover:opacity-70 active:scale-75 transition"
                    >
                      <img
                        src={ForwardIcon}
                        alt=""
                        className="w-3 h-3 xs:w-4 xs:h-4"
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* CONTROLS + WAVEFORM (desktop: prev/play/next next to wave) */}
              <div className="relative flex items-center lg:gap-5 order-1 lg:order-3 flex-1 w-full min-w-0 p-3 py-2 md:py-3 lg:py-5 lg:pl-0 lg:pr-4">
                {/* Desktop: prev/play/next */}
                <div className="hidden lg:flex items-center flex-none shrink-0 gap-1 lg:gap-5 pr-2">
                  <button
                    onClick={() => handleSafeMove("prev")}
                    disabled={currentIndex === 0}
                    aria-label="Previous"
                    className="p-2 hover:opacity-70 active:scale-75 transition disabled:opacity-30"
                  >
                    <img src={BackwardIcon} alt="" className="w-5 h-5" />
                  </button>

                  {!audioLoaded ? (
                    <div className="flex items-center justify-center opacity-50">
                      <img
                        src={LoadingIcon}
                        alt="Loading"
                        className="w-5 h-5 animate-spin"
                      />
                    </div>
                  ) : (
                    <button
                      onClick={togglePlay}
                      aria-label={isPlaying ? "Pause" : "Play"}
                      className="p-2 hover:opacity-70 active:scale-75 transition"
                    >
                      {isPlaying ? (
                        <img src={PauseIcon} alt="Pause" className="w-5 h-5" />
                      ) : (
                        <img src={PlayIcon} alt="Play" className="w-5 h-5" />
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => handleSafeMove("next")}
                    disabled={currentIndex === previewList.length - 1}
                    aria-label="Next"
                    className="p-2 hover:opacity-70 active:scale-75 transition disabled:opacity-30"
                  >
                    <img src={ForwardIcon} alt="" className="w-5 h-5" />
                  </button>
                </div>

                {/* Waveform section: reduced width (narrower segment) */}
                <div className="flex flex-col flex-1 min-w-0 w-full lg:py-3">
                  <div className="relative w-full">
                    {!audioLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center z-10 opacity-50">
                        <img
                          src={LoadingIcon}
                          alt="Loading"
                          className="w-5 h-5 animate-spin"
                        />
                      </div>
                    )}
                    <div
                      ref={waveformRef}
                      className="h-[30px] w-full rounded-md cursor-pointer overflow-hidden relative"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="hidden lg:flex justify-between w-full text-[10px] text-gradient mt-1">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
              </div>

              {/* RIGHT: Autoplay toggle (like SupremeEditsPlayer) */}
              {/* <div className="hidden lg:flex flex-none items-center gap-4 px-5 order-4">
                <div className="flex items-center gap-1">
                  <span className="text-sm text-secondary">Autoplay</span>
                  <label className="relative inline-flex items-center cursor-pointer w-11 h-6">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={autoAdvance}
                      onChange={() => setAutoAdvance((prev) => !prev)}
                      aria-label="Toggle Autoplay"
                    />
                    <div className="w-full h-full bg-[#1E1E1E] rounded-full peer-checked:bg-cyan-500 transition-colors" />
                    <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-[20px]" />
                  </label>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PreviewPlayer;
